import { useCallback, useMemo, useState } from 'react';
import { Form, Nav } from 'react-bootstrap';
import { faFileCsv } from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18n';
import { Modal } from 'components/common/Modal/Modal';
import { FileInput } from 'components/common/FileInput/FileInput';
import { downloadCsv } from 'utils/DownloadUtils';
import {
  buildDeviceImportList,
  DEVICE_IMPORT_SAMPLE_TEXT,
  DEVICE_IMPORT_TEMPLATE_ROWS,
  DeviceImportList,
  parseDeviceImportText
} from './deviceImportList';
import styles from './deviceManagement.module.scss';

enum DeviceImportTab {
  FILE = 'file',
  PASTE = 'paste'
}

/** 兩種來源都吃，另一種是純文字，所以只認副檔名而不是 MIME。 */
const ACCEPTED_TYPES = ['text/csv', 'text/plain', '.csv', '.txt'];

const TEMPLATE_FILE_NAME = 'store_list_template.csv';

type DeviceImportModalProps = {
  onImport: (list: DeviceImportList) => void;
  onClose: () => void;
};

/**
 * 匯入門市名單。
 *
 * 匯入只是畫面上的一個篩選條件，不寫入任何表：解析出店號之後交給清單查詢當作
 * `storeNums` 送出去，與其他篩選條件疊加。
 *
 * 貼上那一段的版面沿用版位限制的 `CustomInputComponent`：一行一筆的輸入框、
 * 下方即時算出辨識到幾筆、錯的那幾行留在原地讓人改。比對邏輯不一樣 —— 那邊比的是
 * 整串名稱相等，這裡比的是店號 —— 所以規則自己寫，呈現照它。
 */
export const DeviceImportModal: React.FC<DeviceImportModalProps> = ({
  onImport,
  onClose
}) => {
  const [tab, setTab] = useState<string>(DeviceImportTab.FILE);
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [file, setFile] = useState<File>();

  const entries = useMemo(() => parseDeviceImportText(text), [text]);
  const recognized = useMemo(
    () => entries.filter((entry) => entry.storeNum !== undefined).length,
    [entries]
  );

  const onFileChange = useCallback((selected: File | undefined) => {
    setFile(selected);
    if (!selected) {
      setFileName('');
      setText('');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setText(String(reader.result ?? ''));
      setFileName(selected.name);
    };
    reader.readAsText(selected, 'utf-8');
  }, []);

  const onTextChange = useCallback((event: any) => {
    setText(event.target.value);
  }, []);

  const onDownloadTemplate = useCallback(() => {
    const [headers, ...rows] = DEVICE_IMPORT_TEMPLATE_ROWS;
    downloadCsv(TEMPLATE_FILE_NAME, headers, rows);
  }, []);

  const onLoadSample = useCallback(() => {
    setTab(DeviceImportTab.PASTE);
    setText(DEVICE_IMPORT_SAMPLE_TEXT);
    setFileName(i18n.t('deviceManagement.labels.importSampleName'));
  }, []);

  const onConfirm = useCallback(() => {
    const list = buildDeviceImportList(
      fileName || i18n.t('deviceManagement.labels.importPastedName'),
      text
    );
    if (!list) {
      return;
    }
    onImport(list);
    onClose();
  }, [fileName, text, onImport, onClose]);

  return (
    <Modal
      title={i18n.t('deviceManagement.labels.importTitle')}
      dismiss={onClose}
      animation={false}
      enableCloseBtn
      primaryButton={{
        title: i18n.t('deviceManagement.buttons.startMatching'),
        disabled: recognized === 0,
        callback: onConfirm
      }}
      secondaryButton={{
        title: i18n.t('common.buttons.cancel'),
        callback: onClose
      }}
    >
      <div className={styles.importContent}>
        <Nav activeKey={tab} onSelect={(key) => key && setTab(key)}>
          <Nav.Item>
            <Nav.Link eventKey={DeviceImportTab.FILE}>
              {i18n.t('deviceManagement.tabs.importFile')}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={DeviceImportTab.PASTE}>
              {i18n.t('deviceManagement.tabs.importPaste')}
            </Nav.Link>
          </Nav.Item>
        </Nav>
        {tab === DeviceImportTab.FILE ? (
          <div className={styles.importFileArea}>
            <FileInput
              validTypes={ACCEPTED_TYPES}
              icon={faFileCsv}
              file={file}
              onChange={onFileChange}
            />
            <div className={styles.importHint}>
              {i18n.t('deviceManagement.hints.importFormat')}
            </div>
            {fileName && (
              <div className={styles.importFileName} data-testid='import-file'>
                {i18n.t('deviceManagement.labels.importReadCount', {
                  fileName,
                  total: recognized
                })}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.importPasteArea}>
            <Form.Control
              as='textarea'
              rows={7}
              value={text}
              onChange={onTextChange}
              placeholder={i18n.t('deviceManagement.placeholders.importPaste')}
              aria-label={i18n.t('deviceManagement.tabs.importPaste')}
            />
            <div className={styles.importHint} data-testid='import-recognized'>
              {i18n.t('deviceManagement.labels.importRecognizedCount', {
                total: recognized
              })}
            </div>
          </div>
        )}
        <div className={styles.importFooter}>
          <span className={styles.importLink} onClick={onDownloadTemplate}>
            {i18n.t('deviceManagement.buttons.downloadImportTemplate')}
          </span>
          <span className={styles.importLink} onClick={onLoadSample}>
            {i18n.t('deviceManagement.buttons.loadImportSample')}
          </span>
          <span className={styles.importMatchKeyHint}>
            {i18n.t('deviceManagement.hints.importMatchKey')}
          </span>
        </div>
      </div>
    </Modal>
  );
};
